<script setup lang="ts">
import { computed } from "vue";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import CodeBlock from "@/components/CodeBlock.vue";

interface Props {
    token: string;
    embedType?: "group";
}

const props = defineProps<Props>();

const inlineCode = computed(() => {
    const options =
        props.embedType === "group"
            ? `{type: 'group', config: {token: '${props.token}'}}`
            : `{config: {token: '${props.token}'}}`;

    return `<div id="zaptime-container"></div>
<${"script"} src="https://iframe.zaptime.app/zaptime.js"></${"script"}>
<${"script"}>
    Zaptime(${options})
        .render('#zaptime-container')
</${"script"}>`;
});

const cmsGuides = [
    {
        id: "wordpress",
        name: "WordPress",
        steps: [
            "event_types.share_hub.cms.wordpress.step1",
            "event_types.share_hub.cms.wordpress.step2",
            "event_types.share_hub.cms.wordpress.step3",
            "event_types.share_hub.cms.wordpress.step4",
        ],
    },
    {
        id: "webflow",
        name: "Webflow",
        steps: [
            "event_types.share_hub.cms.webflow.step1",
            "event_types.share_hub.cms.webflow.step2",
            "event_types.share_hub.cms.webflow.step3",
            "event_types.share_hub.cms.webflow.step4",
        ],
    },
    {
        id: "wix",
        name: "Wix",
        steps: [
            "event_types.share_hub.cms.wix.step1",
            "event_types.share_hub.cms.wix.step2",
            "event_types.share_hub.cms.wix.step3",
            "event_types.share_hub.cms.wix.step4",
        ],
    },
    {
        id: "squarespace",
        name: "Squarespace",
        steps: [
            "event_types.share_hub.cms.squarespace.step1",
            "event_types.share_hub.cms.squarespace.step2",
            "event_types.share_hub.cms.squarespace.step3",
            "event_types.share_hub.cms.squarespace.step4",
        ],
    },
    {
        id: "shopify",
        name: "Shopify",
        steps: [
            "event_types.share_hub.cms.shopify.step1",
            "event_types.share_hub.cms.shopify.step2",
            "event_types.share_hub.cms.shopify.step3",
            "event_types.share_hub.cms.shopify.step4",
        ],
    },
    {
        id: "custom",
        name: "Custom HTML",
        steps: [
            "event_types.share_hub.cms.custom.step1",
            "event_types.share_hub.cms.custom.step2",
            "event_types.share_hub.cms.custom.step3",
        ],
    },
];
</script>

<template>
    <div class="space-y-4">
        <p class="text-sm text-iron-600">
            {{ $t("event_types.share_hub.cms.description") }}
        </p>

        <Accordion type="single" collapsible class="w-full">
            <AccordionItem
                v-for="guide in cmsGuides"
                :key="guide.id"
                :value="guide.id"
            >
                <AccordionTrigger class="text-left hover:no-underline">
                    <span class="font-medium">{{ guide.name }}</span>
                </AccordionTrigger>
                <AccordionContent>
                    <ol
                        class="mb-4 space-y-2 pl-5 text-sm text-iron-600 list-decimal"
                    >
                        <li v-for="(step, index) in guide.steps" :key="index">
                            {{ $t(step) }}
                        </li>
                    </ol>
                    <div class="rounded-lg bg-iron-50 p-3">
                        <p
                            class="mb-2 text-xs font-medium text-iron-500 uppercase"
                        >
                            {{ $t("event_types.share_hub.cms.embed_code") }}
                        </p>
                        <CodeBlock :code="inlineCode" />
                    </div>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    </div>
</template>
